// Agregar al componente MantenedorQuickAddModal
import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Typography,
  IconButton,
  Paper,
  Grid,
  Box,
  TextField,
  CircularProgress,
  Divider
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckboxGroup from "../data/CheckBox";
import { getTipoDocumentoHabilitado } from "../../componentes/http/actions/TipoDocumentoAction";

const MantenedorQuickAddModal = ({ 
  open, 
  onClose, 
  onSubmit, 
  title, 
  fields, 
  isLoading 
}) => {
  const [formData, setFormData] = useState({});
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [selectedTipos, setSelectedTipos] = useState([]);
  const [isLoadingTipos, setIsLoadingTipos] = useState(false);

  // Detectar si es un organismo emisor
  const isOrganismoEmisor = title?.toLowerCase().includes('organismo');

  useEffect(() => {
    if (open) {
      // Inicializar formData basado en los fields
      const initialData = {};
      fields.forEach(field => {
        initialData[field.name] = '';
      });
      setFormData(initialData);

      // Si es organismo emisor, cargar tipos de documento
      if (isOrganismoEmisor) {
        fetchTiposDocumento();
      }
    }
  }, [open, fields, isOrganismoEmisor]);

  const fetchTiposDocumento = async () => {
    try {
      setIsLoadingTipos(true);
      const response = await getTipoDocumentoHabilitado(localStorage.getItem("token"));
      setTiposDocumento(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error al cargar tipos de documento:", error);
      setTiposDocumento([]);
    } finally {
      setIsLoadingTipos(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (event, id) => {
    const { checked } = event.target;
    setSelectedTipos(prev => 
      checked 
        ? [...prev, id]
        : prev.filter(item => item !== id)
    );
  };

  const handleSubmit = async(event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Si es organismo emisor, incluir tipos de documento
    const dataToSubmit = isOrganismoEmisor 
      ? { ...formData, idTipoDocumento: selectedTipos }
      : formData;
    
    try {
      await onSubmit(dataToSubmit);
    } catch (error) {
      console.error("Error en el formulario:", error);
    }
    return false;
  };

  const handleClose = () => {
    setFormData({});
    setSelectedTipos([]);
    onClose();
  };

  return (
    <Modal 
      open={open} 
      onClose={handleClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: isOrganismoEmisor ? '80%' : '60%',
          maxWidth: isOrganismoEmisor ? '800px' : '600px',
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          overflow: 'auto',
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'grey.500',
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h6" component="h2" gutterBottom>
          {title}
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Campos dinámicos */}
            {fields.map((field) => (
              <Grid item xs={12} sm={isOrganismoEmisor ? 6 : 12} key={field.name}>
                <TextField
                  fullWidth
                  label={field.label}
                  name={field.name}
                  value={formData[field.name] || ''}
                  onChange={handleInputChange}
                  required={field.required}
                  margin="normal"
                />
              </Grid>
            ))}
            
            {/* Sección de tipos de documento solo para organismos */}
            {isOrganismoEmisor && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Tipos de Documento Asociados
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Selecciona los tipos de documento que puede emitir este organismo (opcional)
                </Typography>
                
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 2, 
                    mt: 2,
                    bgcolor: 'background.default',
                    borderRadius: 2,
                    minHeight: 200
                  }}
                >
                  {isLoadingTipos ? (
                    <Box display="flex" justifyContent="center" p={3}>
                      <CircularProgress size={24} />
                      <Typography sx={{ ml: 1 }}>Cargando tipos de documento...</Typography>
                    </Box>
                  ) : (
                    <CheckboxGroup
                      title=""
                      items={tiposDocumento}
                      selectedItems={selectedTipos}
                      onChange={handleCheckboxChange}
                      name="tiposDocumento"
                      showSelectAll={true}
                    />
                  )}
                </Paper>
              </Grid>
            )}
          </Grid>

          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: 2, 
              mt: 3,
              borderTop: 1,
              borderColor: 'divider',
              pt: 3
            }}
          >
            <Button 
              onClick={handleClose} 
              variant="outlined"
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Guardando...
                </>
              ) : (
                'Guardar'
              )}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default MantenedorQuickAddModal;